import { devLog, devError } from '../utils/devLogger';
import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import { validateIdNumber, prepareIdNumberForFirestore } from '../utils/idNumberUtils';

const LinkIdNumberModal = ({ show, onHide, onSuccess }) => {
  const { currentUser, fetchUserProfile } = useAuth();
  const [idNumber, setIdNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!idNumber.trim()) {
      setError('Please enter your ID number');
      return;
    }

    const validation = validateIdNumber(idNumber);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setLoading(true);

    try {
      // Prepare ID number for Firestore (ensures it's stored as a string)
      const idNumberToStore = prepareIdNumberForFirestore(validation.normalized);

      // Update user profile in Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        idNumber: idNumberToStore, // Explicitly store as string with leading zeros
        idNumberLinkedAt: new Date().toISOString()
      });

      // Refresh user profile in AuthContext
      await fetchUserProfile(currentUser);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onHide();
    } catch (err) {
      devError('Error updating ID number:', err);
      setError('Failed to update ID number. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Don't allow closing without linking ID number (make it required)
    // User can only close by clicking "Link ID Number" button
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header>
        <Modal.Title>Link Your ID Number</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <strong>Required:</strong> Please link your South African ID number to continue. This is required for document management and verification purposes.
          <div className="mt-2 small">
            <strong>Note:</strong> If you're having trouble with ID validation, please contact your administrator for assistance.
          </div>
        </Alert>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="idNumber">
            <Form.Label>South African ID Number <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter your 13-digit ID number"
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value)}
              maxLength={13}
              required
              disabled={loading}
              pattern="\d{13}"
            />
            <Form.Text className="text-muted">
              Enter your 13-digit South African ID number.<br />
              <strong>Important:</strong> If you were born after 1999, your ID starts with 0 (e.g., 0401051234080)
            </Form.Text>
          </Form.Group>

          <div className="mt-4 d-flex justify-content-end">
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? 'Linking...' : 'Link ID Number'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default LinkIdNumberModal;
